<script setup lang="ts">
import { computed, ref, useAttrs, useId, useTemplateRef, watch } from 'vue';
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  VisuallyHidden,
  type PointerDownOutsideEvent,
} from 'reka-ui';
import { ModalIconColor, ModalSide, ModalSize } from '../modals.constants';
import type { ModalSlotProps, ModalUi } from './modal.types';
import { ModalLeaveSentinel } from './modal-leave-sentinel';
import Icon from '../../icon/icon.vue';
import { IconName, IconSize } from '../../icon/icon.constants';
import IconButton from '../../buttons/icon-button/icon-button.vue';
import Button from '../../buttons/button/button.vue';
import {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant,
} from '../../buttons/buttons.constants';
import { useLabels } from '../../../composables/use-labels';

type Props = {
  /**
   * Names the dialog, and is drawn as the heading unless `hideTitle` is set.
   * Required: a dialog without an accessible name is unusable with a screen
   * reader, and this is the only prop that provides one.
   */
  title: string;
  /**
   * Keeps the title as the dialog's accessible name without drawing it, for
   * panels whose content is the heading: a media preview, an embedded player.
   */
  hideTitle?: boolean;
  /** Supporting copy under the title; also describes the dialog for screen readers. */
  description?: string;
  /**
   * Glyph rendered above the title. Any icon, so a modal can show a trophy for
   * an achievement as easily as a warning. Use the `icon` slot instead when the
   * glyph is a local SVG rather than a shared one.
   */
  icon?: IconName;
  /** Colour of the header glyph; the default on-surface tone when unset. */
  iconColor?: ModalIconColor;
  /** Panel width, MD by default. Ignored when `fullscreen`. */
  size?: ModalSize;
  /**
   * Docks the panel to a viewport edge as a full-height, square-cornered
   * drawer that slides in. Width still follows `size`.
   */
  side?: ModalSide;
  /** Fills the viewport with no radius or max width, e.g. for embedded players. */
  fullscreen?: boolean;
  /** Renders the corner close button. */
  closable?: boolean;
  /**
   * Gives the body its own region: a line under the header and above the
   * footer, the padding moved from the panel onto each region so those lines
   * reach the edges, and a left-aligned header. Undivided panels centre it.
   */
  framed?: boolean;
  /** Keeps the modal open on Escape and on clicks on the overlay. */
  persistent?: boolean;
  /**
   * Drops the footer entirely, for panels whose content carries its own
   * actions or needs none: media previews, players, inline pickers.
   */
  hideFooter?: boolean;
  /** Text of the confirming action; defaults to Save when `asForm`, Delete when `destructive`, else Confirm. */
  confirmLabel?: string;
  /** Text of the cancelling action. */
  cancelLabel?: string;
  /** Colours the confirming action as an error action. */
  destructive?: boolean;
  /** Drops the cancelling action, for notices with a single way out. */
  hideCancel?: boolean;
  /** Drops the confirming action, leaving a lone dismissal. */
  hideConfirm?: boolean;
  /** Disables the confirming action. */
  confirmDisabled?: boolean;
  /** Disables the confirming action while a submission is in flight. */
  submitting?: boolean;
  /**
   * Number of selected items. Appended to the confirming action's label and
   * disables it at zero, for pick-from-a-list modals.
   */
  count?: number;
  /**
   * Turns the confirming action into a native submit for the `<form>` rendered
   * with the body slot's `formId`, and stops the modal closing itself so a
   * failed submission keeps the user's input.
   */
  asForm?: boolean;
  /** Open state when the component mounts, for trigger-slot driven use. */
  defaultOpen?: boolean;
  /** Extra classes per part, for one-off adjustments such as a fixed body height. */
  ui?: ModalUi;
};

const props = withDefaults(defineProps<Props>(), {
  size: ModalSize.MD,
});

defineOptions({ inheritAttrs: false });

const emit = defineEmits<{
  'after:leave': [];
  confirm: [];
  cancel: [];
  /** Resolves `useOverlay` results: true when confirmed, false when cancelled or dismissed. */
  close: [confirmed: boolean];
}>();

const open = defineModel<boolean>('open', { default: undefined });

const slots = defineSlots<{
  /** Element that opens the modal; must be a single root element. */
  activator?: () => unknown;
  /**
   * Replaces the entire panel content, including header, footer, close button
   * and the panel padding, so the slot owns its own spacing.
   */
  content?: (props: ModalSlotProps) => unknown;
  /** Replaces the whole header: the glyph, the title and the description. */
  header?: (props: ModalSlotProps) => unknown;
  /** Replaces the status glyph above the title, keeping the header layout. */
  icon?: () => unknown;
  /** Replaces the title text, keeping the header layout. */
  title?: () => unknown;
  /** Replaces the description text, keeping the header layout. */
  description?: () => unknown;
  /** Main content below the header; `formId` wires a `<form>` to the submit action. */
  body?: (props: ModalSlotProps & { formId: string }) => unknown;
  /** Action row below the body; `formId` wires a custom submit button to the body form. */
  footer?: (props: ModalSlotProps & { formId: string }) => unknown;
}>();

const decided = ref(false);

watch(open, (value) => {
  if (value) {
    decided.value = false;
  }
});

const reportDismissal = (): void => {
  if (decided.value) {
    return;
  }
  decided.value = true;
  emit('close', false);
};

const closeModal = (): void => {
  reportDismissal();
  open.value = false;
};

const onOpenUpdate = (value: boolean): void => {
  if (!value) {
    reportDismissal();
  }
};

const confirm = (): void => {
  emit('confirm');
  if (props.asForm) {
    return;
  }
  decided.value = true;
  emit('close', true);
  open.value = false;
};

const cancel = (): void => {
  decided.value = true;
  emit('cancel');
  emit('close', false);
  open.value = false;
};

const formId = useId();

const slotProps = computed<ModalSlotProps>(() => ({ close: closeModal }));

const attrs = useAttrs();

const labels = useLabels();

// Slot presence is read through functions rather than computeds: `slots` is
// not reactive, so a computed caches the first render and a call site that
// stops passing a slot keeps its stale answer.
// `title` always names the dialog. `hideTitle` only decides whether it is
// drawn, so the name never depends on an attribute falling through.
const hasTitle = (): boolean => Boolean(props.title) || Boolean(slots.title);
const showsTitle = (): boolean => hasTitle() && !props.hideTitle;
const hasDescription = (): boolean =>
  Boolean(props.description) || Boolean(slots.description);
const describedByAttrs = (): Record<string, undefined> =>
  hasDescription() ? {} : { 'aria-describedby': undefined };
const labelledByAttrs = (): Record<string, undefined> =>
  hasTitle() ? { 'aria-label': undefined } : {};
const hiddenTitle = (): string | undefined => {
  if (!props.title) {
    return undefined;
  }
  return props.hideTitle || slots.header || slots.content
    ? props.title
    : undefined;
};

const confirmLabel = computed(() => {
  const base =
    props.confirmLabel ??
    (props.asForm
      ? labels.value.save
      : props.destructive
        ? labels.value.delete
        : labels.value.confirm);
  return props.count ? `${base} (${props.count})` : base;
});

const confirmDisabled = computed(
  () => props.confirmDisabled || props.submitting || props.count === 0,
);

// The footer is the common case, so it is opt-out: 167 of the 205 call sites
// at the time of writing render the standard pair or their own.
const hasFooter = (): boolean => !props.hideFooter;

const preventUnlessDismissible = (event: Event): void => {
  if (!props.persistent) {
    return;
  }
  event.preventDefault();
};

const overlayRef =
  useTemplateRef<InstanceType<typeof DialogOverlay>>('overlay');

/**
 * Only a pointer-down on the backdrop dismisses the modal.
 *
 * Reka exempts layers registered as `DismissableLayerBranch`, which is how
 * Nuxt UI's popovers survive an outside-click. Ours do not register: admin's
 * `Dropdown`/`Tooltip` and the app's `ToolTip`/`PlaybackSpeedMenu` are
 * hand-rolled `Teleport to="body"` layers, so without this a click on a select
 * option inside a modal would read as "outside" and close it. The overlay is
 * always rendered and covers the viewport, so anything stacked above it — a
 * dropdown, a toast, a scrollbar — correctly never dismisses.
 */
const isOnOverlay = (target: EventTarget | null): boolean => {
  const element = overlayRef.value?.$el;
  return (
    element instanceof HTMLElement &&
    target instanceof Node &&
    element.contains(target)
  );
};

const onPointerDownOutside = (event: PointerDownOutsideEvent): void => {
  if (!isOnOverlay(event.detail.originalEvent.target)) {
    event.preventDefault();
    return;
  }
  preventUnlessDismissible(event);
};

const iconColorClass: Record<ModalIconColor, string> = {
  [ModalIconColor.INFO]: 'text-info',
  [ModalIconColor.SUCCESS]: 'text-success',
  [ModalIconColor.WARNING]: 'text-warning',
  [ModalIconColor.ERROR]: 'text-error',
};

const iconClass = computed(() =>
  props.iconColor ? iconColorClass[props.iconColor] : 'text-on-surface-primary',
);

const hasHeader = (): boolean =>
  Boolean(slots.header) ||
  Boolean(slots.icon) ||
  props.icon !== undefined ||
  showsTitle() ||
  hasDescription();

const hasScrollRegion = (): boolean => Boolean(slots.body);

// Widths are fixed dimensions, hardcoded as rem (not from the spacing scale) so
// the panel scales with the user's font size, as all the type inside it does.
const sizeClass: Record<ModalSize, string> = {
  [ModalSize.SM]: 'w-[27.5rem]', // 440px
  [ModalSize.MD]: 'w-[37.5rem]', // 600px
  [ModalSize.LG]: 'w-[58.75rem]', // 940px
  [ModalSize.XL]: 'w-[70rem]', // 1120px
  [ModalSize.XXL]: 'w-[93rem]', // 1488px
};

const sideClass: Record<ModalSide, string> = {
  [ModalSide.LEFT]: 'inset-y-0 left-0',
  [ModalSide.RIGHT]: 'inset-y-0 right-0',
};

const sideTransitionClass: Record<ModalSide, string> = {
  [ModalSide.LEFT]:
    'data-[state=open]:animate-modal-slide-in-left data-[state=closed]:animate-modal-slide-out-left',
  [ModalSide.RIGHT]:
    'data-[state=open]:animate-modal-slide-in-right data-[state=closed]:animate-modal-slide-out-right',
};

const placementClass = computed(() => {
  if (props.fullscreen) {
    return ['inset-0 rounded-none'];
  }
  if (props.side) {
    return [
      sideClass[props.side],
      'h-full rounded-none',
      sizeClass[props.size],
      'max-w-full',
    ];
  }
  return [
    'top-1/2 left-1/2 -translate-1/2 rounded-lg',
    sizeClass[props.size],
    'max-h-[calc(100dvh-var(--spacing-3xl))] max-w-[calc(100%-var(--spacing-3xl))]',
    // A framed panel holds real content, so below `sm` it takes the whole
    // viewport rather than leaving gutters around its own scroll region.
    // `top-0 left-0` and `translate-none` are explicit because `inset-0`
    // alone loses to `top-1/2 left-1/2`: Tailwind emits `inset` first.
    props.framed
      ? 'max-sm:inset-0 max-sm:top-0 max-sm:left-0 max-sm:w-full max-sm:max-w-none max-sm:max-h-none max-sm:translate-none max-sm:rounded-none'
      : '',
  ];
});

const transitionClass = computed(() =>
  props.side && !props.fullscreen
    ? sideTransitionClass[props.side]
    : 'data-[state=open]:animate-modal-in data-[state=closed]:animate-modal-out',
);

const overlayClass = computed(() => [
  'fixed inset-0 z-[9999] bg-[#000]/20 data-[state=open]:animate-modal-fade-in data-[state=closed]:animate-modal-fade-out',
  props.ui?.overlay,
]);

const contentClass = computed(() => [
  'fixed z-[9999] flex flex-col overflow-hidden bg-surface-container text-on-surface-primary shadow-[0_10px_14px_rgba(0,0,0,0.08)] outline-none',
  ...placementClass.value,
  transitionClass.value,
  props.ui?.content,
]);

const regionPadding = 'px-2xl py-2xl sm:px-5xl';

const innerClass = (): unknown[] => [
  'flex min-h-0 flex-1 flex-col overflow-hidden',
  // A full-height panel with no scrolling region has slack and nothing to
  // absorb it, so keep the footer against the bottom edge. A centred panel
  // hugs its content, which makes this a no-op there.
  props.side || props.fullscreen ? 'justify-between' : '',
  slots.content || props.framed
    ? ''
    : 'gap-xl px-2xl pt-3xl pb-3xl sm:px-5xl sm:pt-4xl sm:pb-5xl',
];

// `flex-1` so the region absorbs any slack: in a full-height panel, a drawer
// or fullscreen, that keeps the footer against the bottom edge instead of
// letting it float under short content. A centred panel hugs, so there is no
// slack to absorb and nothing changes.
const scrollClass = computed(() => [
  'flex min-h-0 flex-1 flex-col gap-xl overflow-y-auto',
  props.framed ? regionPadding : '',
]);

// Framing and alignment travel together: a divided panel leads with its title,
// an undivided one centres the whole block.
const alignClass = computed(() =>
  props.framed ? 'items-start text-left' : 'items-center text-center',
);

const headerClass = computed(() => [
  'flex shrink-0',
  'flex-col',
  alignClass.value,
  props.framed
    ? [
        'gap-md',
        regionPadding,
        // the close reaches 44px in from the edge; region padding is only 24 below `sm`
        props.closable ? 'pr-5xl' : '',
        'border-b border-line',
      ]
    : 'gap-xl',
  props.ui?.header,
]);

const descriptionClass = 'shrink-0 text-body-lg';

const bodyClass = computed(() => [props.ui?.body]);

const footerClass = computed(() => [
  'flex shrink-0 flex-wrap items-center gap-lg',
  props.framed
    ? [
        'justify-end',
        hasScrollRegion() ? 'border-t border-line' : '',
        regionPadding,
      ]
    : [props.side ? 'justify-end' : 'justify-center', 'mt-xl'],
  props.ui?.footer,
]);
</script>

<template>
  <DialogRoot
    v-model:open="open"
    :default-open="props.defaultOpen"
    @update:open="onOpenUpdate"
  >
    <DialogTrigger v-if="slots.activator" as-child>
      <slot name="activator" />
    </DialogTrigger>
    <DialogPortal>
      <DialogOverlay ref="overlay" :class="overlayClass" />
      <DialogContent
        v-bind="{ ...attrs, ...describedByAttrs(), ...labelledByAttrs() }"
        :class="contentClass"
        @escape-key-down="preventUnlessDismissible"
        @pointer-down-outside="onPointerDownOutside"
      >
        <ModalLeaveSentinel @leave="emit('after:leave')" />
        <VisuallyHidden v-if="hiddenTitle()">
          <DialogTitle>{{ hiddenTitle() }}</DialogTitle>
        </VisuallyHidden>
        <IconButton
          v-if="props.closable && !slots.content"
          :icon-name="IconName.CLOSE"
          :label="labels.close"
          :size="ButtonSize.SM"
          :variant="ButtonVariant.TEXT"
          :color="ButtonColor.SURFACE_CONTAINER"
          class="absolute top-lg right-lg z-10"
          @click="closeModal"
        />
        <div :class="innerClass()">
          <slot name="content" v-bind="slotProps">
            <div v-if="hasHeader()" :class="headerClass">
              <slot name="header" v-bind="slotProps">
                <slot name="icon">
                  <Icon
                    v-if="props.icon"
                    :name="props.icon"
                    :size="IconSize.LG"
                    :class="iconClass"
                  />
                </slot>
                <DialogTitle v-if="showsTitle()" class="text-heading-md">
                  <slot name="title">{{ props.title }}</slot>
                </DialogTitle>
                <DialogDescription
                  v-if="hasDescription()"
                  :class="descriptionClass"
                >
                  <slot name="description">{{ props.description }}</slot>
                </DialogDescription>
              </slot>
            </div>
            <div v-if="hasScrollRegion()" :class="scrollClass">
              <div v-if="slots.body" :class="bodyClass">
                <slot name="body" v-bind="slotProps" :form-id="formId" />
              </div>
            </div>
            <div v-if="hasFooter()" :class="footerClass">
              <slot name="footer" v-bind="slotProps" :form-id="formId">
                <Button
                  v-if="!props.hideCancel"
                  :label="props.cancelLabel ?? labels.cancel"
                  :variant="ButtonVariant.OUTLINED"
                  :color="ButtonColor.SECONDARY"
                  @click="cancel"
                />
                <Button
                  v-if="!props.hideConfirm"
                  :label="confirmLabel"
                  :color="
                    props.destructive ? ButtonColor.ERROR : ButtonColor.PRIMARY
                  "
                  :disabled="confirmDisabled"
                  :type="props.asForm ? ButtonType.SUBMIT : ButtonType.BUTTON"
                  :form="props.asForm ? formId : undefined"
                  @click="props.asForm ? undefined : confirm()"
                />
              </slot>
            </div>
          </slot>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
